import { useEffect, useRef, useState } from 'react';
import { useDebounce } from '@uidotdev/usehooks';
import { FormOption } from '../types/Form';

type UseServerSearchOptionsArgs = {
  isOpen: boolean;
  searchQuery: string;
  onSearchFilter: (query: string) => Promise<FormOption[]>;
  fetchOnMount?: boolean;
  minSearchLength?: number;
};

/**
 * Runs a debounced server search while the dropdown is open and returns the
 * resulting options + loading state.
 *
 * - Keying off `isOpen` means reopening re-runs the search, so a failed load is
 *   retried by closing and reopening the picker.
 * - `fetchOnMount` additionally runs the initial (empty-query) search as soon as
 *   the dropdown mounts, so options are ready before the user opens it. In this
 *   mode opening the picker doesn't re-run a search it has already fetched for
 *   the current query — the options are already there.
 * - `shouldApply` discards stale responses: out-of-order ones (a newer search
 *   already started) and ones that resolve mid-debounce after the user has
 *   typed past the query they were fetched for.
 */
export const useServerSearchOptions = ({
  isOpen,
  searchQuery,
  onSearchFilter,
  fetchOnMount = false,
  minSearchLength = 0
}: UseServerSearchOptionsArgs) => {
  const [options, setOptions] = useState<FormOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Refs so the effect depends only on the query to fetch — not on the (often
  // inline) callback or the live input value.
  const onSearchFilterRef = useRef(onSearchFilter);
  onSearchFilterRef.current = onSearchFilter;
  const searchQueryRef = useRef(searchQuery);
  searchQueryRef.current = searchQuery;

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // An eager picker fetches from mount; a lazy one only once opened.
  const shouldFetch = fetchOnMount || isOpen;
  // A non-empty query shorter than the minimum doesn't search (the empty query
  // is exempt, so the initial/cleared list still loads).
  const isBelowMinLength =
    debouncedSearchQuery.length > 0 &&
    debouncedSearchQuery.length < minSearchLength;
  // The query to fetch for, or `null` when nothing should be fetched. Keying the
  // effect on this (rather than `isOpen`) means an eager picker only re-fetches
  // when the query changes — opening it doesn't tear down / re-run an
  // already-loaded search — while a lazy picker still re-runs (retries) on open.
  const activeQuery =
    shouldFetch && !isBelowMinLength ? debouncedSearchQuery : null;

  useEffect(() => {
    if (activeQuery === null) {
      setIsLoading(false);
      // Drop any prior results for a sub-threshold query, so a stale filtered
      // list isn't shown behind the "type at least N characters" hint.
      if (isBelowMinLength) setOptions([]);
      return undefined;
    }

    let isCurrent = true;
    const query = activeQuery;
    const shouldApply = () => isCurrent && searchQueryRef.current === query;

    const runSearch = async () => {
      setIsLoading(true);
      try {
        const result = await onSearchFilterRef.current(query);
        if (shouldApply()) setOptions(result);
      } catch {
        // The caller surfaces the failure (via onSearchFilter); swallow here so
        // a rejected search isn't an unhandled rejection, and a reopen retries.
        if (shouldApply()) setOptions([]);
      } finally {
        if (shouldApply()) setIsLoading(false);
      }
    };

    void runSearch();

    return () => {
      isCurrent = false;
    };
  }, [activeQuery, isBelowMinLength]);

  return { options, isLoading };
};
